import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Box, Paper, Typography, CircularProgress } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CardRenderer from "../renderer/CardRenderer";
import {
  clearSubmissionRequestId,
  getSubmissionRequestId,
} from "./submissionRequestId";

/**
 * Página pública do paciente. Renderizada FORA do shell autenticado
 * (sem Menu/Header/MSAL). O acesso é controlado pelo token do convite.
 */
// Identificação que o Dashboard usa para listar quem começou pelo link e não terminou.
// São os nomes dos campos obrigatórios do APA; formulário sem eles simplesmente não registra.
const CAMPOS_IDENTIFICACAO = { cpf: "cpf", nome: "nomecompleto157", surgeon: "surgeon", phone: "wpp" };

// DDD + 8 ou 9 dígitos. Sem este piso o registro saía no meio da digitação e o painel
// listava "(4" como telefone de contato.
const TELEFONE_MINIMO = 10;

export function identificacaoParcial (definition, values) {
  const porNome = Object.fromEntries(((definition && definition.fields) || []).map((f) => [f.name, f.id]));
  const dados = {};
  for (const [chave, nome] of Object.entries(CAMPOS_IDENTIFICACAO)) {
    const bruto = values[porNome[nome]];
    // CPF de estrangeiro chega como objeto: o painel guarda o documento que a pessoa deu.
    const valor = bruto && typeof bruto === "object" ? bruto.doc : bruto;
    const texto = valor === null || valor === undefined ? "" : String(valor).trim();
    if (!texto) return null;
    dados[chave] = texto;
  }
  if (dados.phone.replace(/\D/g, "").length < TELEFONE_MINIMO) return null;
  return dados;
}

export default function PublicForm({ token, mode = "invite" }) {
  const base = mode === "open" ? `/public/forms/open/${token}` : `/public/forms/${token}`;
  const [api, setApi] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | completed | expired | error | done
  const [message, setMessage] = useState("");
  const [data, setData] = useState(null);
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const successRef = useRef(null);
  const parcialEnviada = useRef(null);

  // monta o client a partir do config.json (mesma fonte usada pelo app)
  useEffect(() => {
    // OBS: a instância do axios é uma FUNÇÃO; precisa do updater (() => ...)
    // senão o React a interpreta como functional update e a executa.
    fetch("/config.json")
      .then((r) => r.json())
      .then((cfg) => {
        const baseURL = cfg.TAKAOKABOTS_API_URL || process.env.REACT_APP_TAKAOKABOTS_API_URL;
        setApi(() => axios.create({ baseURL, headers: { Accept: "application/json" } }));
      })
      .catch(() => {
        setApi(() => axios.create({ baseURL: process.env.REACT_APP_TAKAOKABOTS_API_URL, headers: { Accept: "application/json" } }));
      });
  }, []);

  useEffect(() => {
    if (!api) return;
    // A definição é editada in-place, então cópia guardada em qualquer ponto do caminho
    // (proxy de operadora, cache compartilhado) devolve pergunta que já saiu do formulário
    // — e o no-store da API só vale para as respostas que ela chegar a servir. URL nova é
    // chave de cache nova: garante a ida ao servidor mesmo onde já exista cópia gravada.
    api.get(base, { params: { _: Date.now() } }).then(
      (resp) => {
        if (resp.data.completed) { setStatus("completed"); setMessage(resp.data.message); return; }
        setData(resp.data);
        // pré-preenche o telefone do contato (quando o convite foi enviado por WhatsApp)
        const phone = resp.data?.contact?.phone;
        const phoneField = (resp.data?.definition?.fields || []).find((f) => f.type === "phone");
        if (phone && phoneField) {
          setValues((prev) => (prev[phoneField.id] ? prev : { ...prev, [phoneField.id]: phone }));
        }
        setStatus("ready");
      },
      (error) => {
        const d = error?.response?.data;
        if (error?.response?.status === 410) { setStatus("expired"); setMessage(d?.message || "Formulário expirado"); }
        else { setStatus("error"); setMessage(d?.message || "Não foi possível carregar o formulário"); }
      }
    );
  }, [api, base]);

  useEffect(() => {
    if (status === "done") successRef.current && successRef.current.focus();
  }, [status]);

  // Registra a identificação assim que ela fica completa. Sem isto, quem abre o link e
  // desiste no meio não deixa rastro nenhum — nem para o painel. Só no link aberto: pelo
  // convite o paciente já tem nome e telefone gravados.
  // Reenvia quando a identificação muda (a rota faz upsert pelo request_id): parar no
  // primeiro envio congelava o dado no estado em que ele estava ao ficar completo, e quem
  // corrigia o telefone ou o nome depois aparecia errado no painel.
  useEffect(() => {
    if (mode !== "open" || status !== "ready" || !api) return;
    const dados = identificacaoParcial(data && data.definition, values);
    if (!dados) return;
    const assinatura = JSON.stringify(dados);
    if (assinatura === parcialEnviada.current) return;
    const id = setTimeout(() => {
      parcialEnviada.current = assinatura;
      api.post(`${base}/parcial`, { ...dados, request_id: getSubmissionRequestId(mode, token) })
        .catch(() => { parcialEnviada.current = null; });
    }, 800);
    return () => clearTimeout(id);
  }, [api, base, data, mode, status, token, values]);

  function handleSubmit(finalValues) {
    setSubmitting(true);
    setMessage("");
    const requestId = getSubmissionRequestId(mode, token);
    api.post(`${base}/submit`, { values: finalValues, request_id: requestId }).then(
      () => {
        clearSubmissionRequestId(mode, token);
        setStatus("done");
        setSubmitting(false);
      },
      (error) => {
        setSubmitting(false);
        const d = error?.response?.data;
        if (error?.response?.status === 409) {
          clearSubmissionRequestId(mode, token);
          setStatus("completed");
          setMessage(d?.message);
        }
        else setMessage(d?.message || "Erro ao enviar respostas. Verifique os campos.");
      }
    );
  }

  // Gate APA: ao sair do campo CPF, pergunta ao backend se o paciente ainda precisa responder.
  async function onCheckApa(field, value) {
    const isForeign = value && typeof value === "object" && value.foreign;
    const documento = isForeign
      ? { rne: value.doc, nationality: value.nationality }
      : { cpf: value };
    try {
      // POST dentro do escopo do link: o documento vai no corpo, não na query string,
      // que ficaria no access log do proxy, no histórico e no header Referer.
      const resp = await api.post(`${base}/apa/check`, documento);
      const { seguir, motivo } = resp.data || {};
      if (seguir) return { proceed: true };
      if (motivo === "cpf_invalido") {
        return { proceed: false, error: "CPF inválido. Confira os números digitados." };
      }
      if (motivo === "expirado") {
        setStatus("expired");
        setMessage("Sua avaliação pré-anestésica está expirada. Por favor, entre em contato com a equipe.");
        return { proceed: false };
      }
      // ja_respondido (ou qualquer outro bloqueio) — tela de "já respondido"
      setStatus("completed");
      setMessage("Este questionário já foi respondido para o seu procedimento.");
      return { proceed: false };
    } catch (e) {
      return { proceed: false, error: "Não foi possível verificar o CPF agora. Tente novamente." };
    }
  }

  const Shell = ({ children }) => (
    <Box sx={{ minHeight: "100vh", background: "#f0f2f5", py: { xs: 2, md: 6 }, px: 2 }}>
      <Box sx={{ maxWidth: 720, mx: "auto" }}>{children}</Box>
    </Box>
  );

  if (status === "loading") {
    return <Shell><Box sx={{ textAlign: "center", pt: 10 }}><CircularProgress /></Box></Shell>;
  }

  if (status === "done") {
    return (
      <Box sx={{
        minHeight: "100vh",
        background: "radial-gradient(circle at 50% 20%, #eef5ff 0%, #dfe9fb 45%, #d4e2f7 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
      }}>
        <Paper ref={successRef} tabIndex={-1} role="status" aria-live="polite" elevation={0} sx={{
          width: "100%",
          maxWidth: 720,
          position: "relative",
          overflow: "hidden",
          borderRadius: 4,
          px: { xs: 3, md: 7 },
          py: { xs: 5, md: 7 },
          textAlign: "center",
          background: "linear-gradient(180deg, #f8fbff 0%, #eef5ff 100%)",
          border: "1px solid rgba(19, 51, 95, 0.12)",
          boxShadow: "0 24px 70px rgba(19, 51, 95, 0.18)",
          animation: "apaSuccessCard .55s cubic-bezier(0.16, 1, 0.3, 1) both",
          "@keyframes apaSuccessCard": {
            from: { opacity: 0, transform: "translateY(18px) scale(0.98)" },
            to: { opacity: 1, transform: "translateY(0) scale(1)" },
          },
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
            "& *, & *::before, & *::after": { animation: "none !important" },
          },
          "&:before": {
            content: '""',
            position: "absolute",
            inset: -120,
            background: "radial-gradient(circle, rgba(46, 125, 50, 0.16) 0%, rgba(46, 125, 50, 0) 58%)",
            pointerEvents: "none",
          },
        }}>
          <Box sx={{
            width: 104,
            height: 104,
            mx: "auto",
            mb: 3,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            position: "relative",
            background: "linear-gradient(135deg, #e8f5e9 0%, #ffffff 100%)",
            boxShadow: "0 16px 36px rgba(46, 125, 50, 0.24)",
            "&:before": {
              content: '""',
              position: "absolute",
              inset: -10,
              border: "2px solid rgba(46, 125, 50, 0.18)",
              borderRadius: "50%",
              animation: "apaSuccessRing 1.4s ease-out both",
            },
            "@keyframes apaSuccessRing": {
              from: { opacity: 0.8, transform: "scale(0.75)" },
              to: { opacity: 0, transform: "scale(1.28)" },
            },
          }}>
            <CheckCircleIcon sx={{
              fontSize: 68,
              color: "#2e7d32",
              filter: "drop-shadow(0 6px 10px rgba(46, 125, 50, 0.24))",
              animation: "apaSuccessCheck .55s .15s cubic-bezier(0.16, 1, 0.3, 1) both",
              "@keyframes apaSuccessCheck": {
                from: { opacity: 0, transform: "scale(0.65) rotate(-12deg)" },
                to: { opacity: 1, transform: "scale(1) rotate(0)" },
              },
            }} />
          </Box>

          <Typography variant="overline" sx={{
            color: "#2e7d32",
            fontWeight: 800,
            letterSpacing: "0.14em",
          }}>
            Questionário enviado
          </Typography>

          <Typography variant="h3" sx={{
            mt: 1,
            color: "#13335f",
            fontWeight: 700,
            fontSize: { xs: "2rem", md: "2.65rem" },
            lineHeight: 1.12,
          }}>
            Avaliação Finalizada com Sucesso!
          </Typography>

          <Typography sx={{
            mt: 2.5,
            color: "#40526d",
            fontSize: { xs: "1rem", md: "1.08rem" },
            lineHeight: 1.75,
            maxWidth: 560,
            mx: "auto",
          }}>
            Obrigado(a) por completar o Questionário Digital. Nossa equipe médica avaliará suas informações e, se necessário, entrará em contato para mais esclarecimentos.
          </Typography>

          <Box sx={{
            mt: 4,
            mx: "auto",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            maxWidth: "100%",
            boxSizing: "border-box",
            gap: 1,
            px: 2.5,
            py: 1.25,
            borderRadius: 999,
            background: "#ffffff",
            color: "#13335f",
            border: "1px solid rgba(19, 51, 95, 0.12)",
            boxShadow: "0 10px 24px rgba(19, 51, 95, 0.08)",
            fontSize: { xs: "0.92rem", md: "1rem" },
          }}>
            <Box component="span" sx={{ color: "#2e7d32", fontWeight: 800 }}>WhatsApp</Box>
            <Box component="span">Agendamentos: <strong>(11) 97605-9410</strong></Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  if (status === "completed" || status === "expired" || status === "error") {
    // mesmo sistema visual da tela de sucesso, variando ícone/cor/título por estado
    const view = {
      completed: {
        Icon: TaskAltIcon, color: "#2e7d32", rgb: "46, 125, 50",
        overline: "Tudo certo", title: "Questionário já respondido",
        fallback: "Este questionário já foi respondido.",
      },
      expired: {
        Icon: AccessTimeIcon, color: "#ed6c02", rgb: "237, 108, 2",
        overline: "Prazo encerrado", title: "Avaliação expirada",
        fallback: "Esta avaliação pré-anestésica está expirada.",
      },
      error: {
        Icon: ErrorOutlineIcon, color: "#c62828", rgb: "198, 40, 40",
        overline: "Não foi possível abrir", title: "Formulário indisponível",
        fallback: "Não foi possível carregar o formulário.",
      },
    }[status];
    const StatusIcon = view.Icon;
    return (
      <Box sx={{
        minHeight: "100vh",
        background: "radial-gradient(circle at 50% 20%, #eef5ff 0%, #dfe9fb 45%, #d4e2f7 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
      }}>
        <Paper role="status" aria-live="polite" elevation={0} sx={{
          width: "100%",
          maxWidth: 720,
          position: "relative",
          overflow: "hidden",
          borderRadius: 4,
          px: { xs: 3, md: 7 },
          py: { xs: 5, md: 7 },
          textAlign: "center",
          background: "linear-gradient(180deg, #f8fbff 0%, #eef5ff 100%)",
          border: "1px solid rgba(19, 51, 95, 0.12)",
          boxShadow: "0 24px 70px rgba(19, 51, 95, 0.18)",
          animation: "apaStatusCard .55s cubic-bezier(0.16, 1, 0.3, 1) both",
          "@keyframes apaStatusCard": {
            from: { opacity: 0, transform: "translateY(18px) scale(0.98)" },
            to: { opacity: 1, transform: "translateY(0) scale(1)" },
          },
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
            "& *, & *::before, & *::after": { animation: "none !important" },
          },
          "&:before": {
            content: '""',
            position: "absolute",
            inset: -120,
            background: `radial-gradient(circle, rgba(${view.rgb}, 0.14) 0%, rgba(${view.rgb}, 0) 58%)`,
            pointerEvents: "none",
          },
        }}>
          <Box sx={{
            width: 104,
            height: 104,
            mx: "auto",
            mb: 3,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            background: `linear-gradient(135deg, rgba(${view.rgb}, 0.14) 0%, #ffffff 100%)`,
            boxShadow: `0 16px 36px rgba(${view.rgb}, 0.22)`,
          }}>
            <StatusIcon sx={{
              fontSize: 68,
              color: view.color,
              filter: `drop-shadow(0 6px 10px rgba(${view.rgb}, 0.24))`,
            }} />
          </Box>

          <Typography variant="overline" sx={{
            color: view.color,
            fontWeight: 800,
            letterSpacing: "0.14em",
          }}>
            {view.overline}
          </Typography>

          <Typography variant="h3" sx={{
            mt: 1,
            color: "#13335f",
            fontWeight: 700,
            fontSize: { xs: "1.9rem", md: "2.4rem" },
            lineHeight: 1.12,
          }}>
            {view.title}
          </Typography>

          <Typography sx={{
            mt: 2.5,
            color: "#40526d",
            fontSize: { xs: "1rem", md: "1.08rem" },
            lineHeight: 1.75,
            maxWidth: 560,
            mx: "auto",
          }}>
            {message || view.fallback}
          </Typography>

          <Box sx={{
            mt: 4,
            mx: "auto",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            maxWidth: "100%",
            boxSizing: "border-box",
            gap: 1,
            px: 2.5,
            py: 1.25,
            borderRadius: 999,
            background: "#ffffff",
            color: "#13335f",
            border: "1px solid rgba(19, 51, 95, 0.12)",
            boxShadow: "0 10px 24px rgba(19, 51, 95, 0.08)",
            fontSize: { xs: "0.92rem", md: "1rem" },
          }}>
            <Box component="span" sx={{ color: "#2e7d32", fontWeight: 800 }}>WhatsApp</Box>
            <Box component="span">Agendamentos: <strong>(11) 97605-9410</strong></Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  // status === ready — experiência "card" (uma pergunta por vez)
  return (
    <CardRenderer
      definition={data.definition}
      values={values}
      onChange={(id, v) => setValues((prev) => ({ ...prev, [id]: v }))}
      submitting={submitting}
      onSubmit={handleSubmit}
      onCheckApa={onCheckApa}
      submitError={message}
      title={data.form.title}
      description={data.form.description}
    />
  );
}
